/* Nooni theme scripts: sliders, pack picker, FAQ "show more", sticky add to cart */
(() => {
  if (!customElements.get('nn-slider')) {
    class LkSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-nn-track]');
        this.prev = this.querySelector('[data-nn-prev]');
        this.next = this.querySelector('[data-nn-next]');
        this.dotsWrap = this.querySelector('[data-nn-dots]');
        this.nav = this.querySelector('.nn-slider__nav');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        this.ro = new ResizeObserver(() => this.build());
        this.ro.observe(this.track);
        requestAnimationFrame(() => this.build());
        window.addEventListener('load', () => this.build(), { once: true });
      }
      disconnectedCallback() { this.ro?.disconnect(); }
      perView() {
        const first = this.slides[0];
        const w = first ? first.getBoundingClientRect().width : 0;
        if (!w) return 1;
        return Math.max(1, Math.round(this.track.clientWidth / w));
      }
      pages() { return Math.max(1, this.slides.length - this.perView() + 1); }
      build() {
        const pages = this.pages();
        if (pages === this.builtPages && this.dotsWrap?.children.length === pages) return this.update();
        this.builtPages = pages;
        if (this.nav) this.nav.hidden = pages <= 1;
        if (!this.dotsWrap) return this.update();
        this.dotsWrap.innerHTML = '';
        for (let i = 0; i < pages; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'nn-slider__dot';
          b.setAttribute('aria-label', `${i + 1} / ${pages}`);
          b.addEventListener('click', () => this.to(i));
          this.dotsWrap.appendChild(b);
        }
        this.update();
      }
      index() {
        const w = this.slides[0]?.getBoundingClientRect().width || 1;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max > 0 && this.track.scrollLeft >= max - 2) return this.pages() - 1;
        return Math.round(this.track.scrollLeft / (w + gap));
      }
      to(i) {
        const s = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
        if (!s) return;
        const cs = getComputedStyle(this.track);
        const pad = parseFloat(cs.scrollPaddingLeft) || parseFloat(cs.paddingLeft) || 0;
        const left = this.track.scrollLeft + s.getBoundingClientRect().left - this.track.getBoundingClientRect().left - pad;
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
      }
      go(dir) { this.to(this.index() + dir); }
      update() {
        const i = Math.min(this.index(), this.pages() - 1);
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        this.dotsWrap?.querySelectorAll('button').forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      }
    }
    customElements.define('nn-slider', LkSlider);
  }

  if (!customElements.get('nn-pack-picker')) {
    class NnPackPicker extends HTMLElement {
      connectedCallback() {
        this.format = this.dataset.moneyFormat || '{{amount}} €';
        this.packs = Array.from(this.querySelectorAll('.nn-pp__pack input'));
        this.slots = Array.from(this.querySelectorAll('[data-slot]'));
        this.atc = this.querySelector('[data-pp-atc]');
        this.error = this.querySelector('[data-pp-error]');
        this.addEventListener('change', () => this.update());
        this.atc?.addEventListener('click', () => this.add());
        this.update();
      }
      money(cents) {
        const fmt = (n, dec, t, d) => {
          const [i, f] = (n / 100).toFixed(dec).split('.');
          return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? d + f : '');
        };
        return this.format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
          amount: fmt(cents, 2, ',', '.'),
          amount_no_decimals: fmt(cents, 0, ',', '.'),
          amount_with_comma_separator: fmt(cents, 2, '.', ','),
          amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
          amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
          amount_with_space_separator: fmt(cents, 2, ' ', ','),
        }[k] ?? fmt(cents, 2, ',', '.')));
      }
      qty() { const c = this.packs.find((p) => p.checked); return c ? parseInt(c.value, 10) : 1; }
      chosen(slot) { return slot.querySelector('input:checked'); }
      update() {
        const qty = this.qty();
        this.slots.forEach((slot) => {
          const n = parseInt(slot.dataset.slot, 10);
          if (slot.tagName === 'FIELDSET') slot.hidden = n > qty;
          const c = this.chosen(slot);
          const name = slot.querySelector('[data-pp-name]');
          if (name && c) name.textContent = c.dataset.name;
        });
        // per-pack prices follow the characters picked in slot 1..n
        this.packs.forEach((input) => {
          const n = parseInt(input.value, 10);
          const pct = parseFloat(input.dataset.discount) || 0;
          let total = 0, compare = 0;
          for (let i = 0; i < n; i++) {
            const c = this.slots[i] && this.chosen(this.slots[i]) || this.chosen(this.slots[0]);
            if (!c) continue;
            const price = parseInt(c.dataset.price, 10);
            total += price; compare += Math.max(price, parseInt(c.dataset.compare, 10) || price);
          }
          total = Math.round(total * (100 - pct) / 100);
          const box = input.closest('.nn-pp__pack');
          const t = box.querySelector('[data-pp-total]'), s = box.querySelector('[data-pp-compare]');
          if (t) t.textContent = this.money(total);
          if (s) { s.textContent = this.money(compare); s.hidden = compare <= total; }
          if (input.checked) {
            const a = this.querySelector('[data-pp-atc-total]');
            if (a) a.textContent = this.money(total);
          }
        });
      }
      async add() {
        const qty = this.qty();
        const map = new Map();
        for (let i = 0; i < qty; i++) {
          const c = this.slots[i] && this.chosen(this.slots[i]);
          if (!c) continue;
          map.set(c.value, (map.get(c.value) || 0) + 1);
        }
        const items = Array.from(map, ([id, quantity]) => ({ id: parseInt(id, 10), quantity }));
        if (!items.length) return;
        this.atc.disabled = true;
        this.atc.setAttribute('aria-busy', 'true');
        if (this.error) this.error.textContent = '';
        try {
          const res = await fetch(`${this.dataset.cartAdd}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || 'Erreur');
          window.location.href = this.dataset.cartUrl;
        } catch (e) {
          if (this.error) this.error.textContent = e.message;
          this.atc.disabled = false;
          this.atc.removeAttribute('aria-busy');
        }
      }
    }
    customElements.define('nn-pack-picker', NnPackPicker);
  }

  if (!customElements.get('nn-sticky-atc')) {
    class UvStickyAtc extends HTMLElement {
      connectedCallback() {
        this.target = document.querySelector('product-info .nn-pp__atc, product-info .product-form__buttons, .product-form__buttons, product-form');
        this.state = { targetVisible: true, scrolled: false };
        if (this.target) {
          this.io = new IntersectionObserver(([e]) => {
            this.state.targetVisible = e.isIntersecting;
            this.render();
          });
          this.io.observe(this.target);
        } else {
          this.state.targetVisible = false;
        }
        this.onScroll = () => {
          this.state.scrolled = window.scrollY > window.innerHeight * 0.8;
          if (this.target) {
            const r = this.target.getBoundingClientRect();
            this.state.targetVisible = r.bottom > 0 && r.top < window.innerHeight;
          }
          this.render();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
        this.querySelector('[data-nn-scroll-buy]')?.addEventListener('click', (e) => {
          if (!this.target) return;
          e.preventDefault();
          this.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
      disconnectedCallback() { this.io?.disconnect(); window.removeEventListener('scroll', this.onScroll); }
      render() {
        const show = this.state.scrolled && !this.state.targetVisible;
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('inert', !show);
      }
    }
    customElements.define('nn-sticky-atc', UvStickyAtc);
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-nn-faq-more]');
    if (!btn) return;
    const list = document.getElementById(btn.getAttribute('aria-controls'));
    list?.querySelectorAll('[data-nn-more]').forEach((el) => (el.hidden = false));
    btn.closest('.nn-faq__more')?.remove();
  });
})();
